import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, query, where, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import ContactActions from './ContactActions';
import TagManager from './TagManager';
import RecordFilter from './RecordFilter';
import TeamManager from './TeamManager';
import ShareToTeamModal from './ShareToTeamModal';
import { API_BASE, getAuthHeader } from './services/api';

const HistoryView = () => {
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [managingTags, setManagingTags] = useState(null);
  const [showTeamManager, setShowTeamManager] = useState(false);
  const [sharingRecord, setSharingRecord] = useState(null);
  const recordsPerPage = 10;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        fetchUserRecords(currentUser.uid);
      } else {
        setRecords([]);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchUserRecords = async (userId) => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'businessCardsnotenew'),
        where('userId', '==', userId)
      );
      const querySnapshot = await getDocs(q);
      const userRecords = [];
      querySnapshot.forEach((doc) => {
        userRecords.push({ id: doc.id, ...doc.data() });
      });
      
      // 在客戶端排序，避免需要複合索引
      userRecords.sort((a, b) => {
        const aTime = a.timestamp?.toDate?.() || new Date(a.timestamp || 0);
        const bTime = b.timestamp?.toDate?.() || new Date(b.timestamp || 0);
        return bTime - aTime; // 降序排列（最新的在前）
      });
      
      setRecords(userRecords);
      setFilteredRecords(userRecords);
    } catch (error) {
      console.error('Error fetching records:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '無日期';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('zh-TW') + ' ' + date.toLocaleTimeString('zh-TW');
  };

  // 分頁相關計算
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentRecords = filteredRecords.slice(startIndex, endIndex);

  const handleFilterChange = (filtered) => {
    setFilteredRecords(filtered);
    setCurrentPage(1); // 重置到第一頁
  };

  const handleTagsUpdate = (recordId, newTags) => {
    setRecords(prev => prev.map(record => 
      record.id === recordId ? { ...record, tags: newTags } : record
    ));
    setFilteredRecords(prev => prev.map(record => 
      record.id === recordId ? { ...record, tags: newTags } : record
    ));
    setManagingTags(null);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    setSelectedRecord(null); // 切換頁面時關閉詳細資料
  };

  const getDisplayName = (record) => {
    // 優先顯示姓名，其次公司名稱，最後顯示記錄ID
    return record['姓名'] || record['Name'] || record['公司名稱'] || record['Company Name'] || `記錄 ${record.id.substring(0, 8)}`;
  };

  const handleDeleteRecord = async (recordId) => {
    if (window.confirm('確定要刪除這筆記錄嗎？此操作無法復原。')) {
      try {
        await deleteDoc(doc(db, 'businessCardsnotenew', recordId));
        const updatedRecords = records.filter(record => record.id !== recordId);
        setRecords(updatedRecords);
        setFilteredRecords(updatedRecords);
        setSelectedRecord(null);
        alert('記錄已成功刪除');
      } catch (error) {
        console.error('刪除記錄失敗:', error);
        alert('刪除失敗，請重試');
      }
    }
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record.id);
    const editableFields = { ...record };
    // 移除不可編輯的系統字段
    delete editableFields.id;
    delete editableFields.userId;
    delete editableFields.userEmail;
    delete editableFields.userName;
    delete editableFields.timestamp;
    delete editableFields.imageUrl;
    setEditFormData(editableFields);
  };

  const handleSaveEdit = async () => {
    try {
      const recordRef = doc(db, 'businessCardsnotenew', editingRecord);
      await updateDoc(recordRef, editFormData);
      
      // 更新本地state
      const updatedRecords = records.map(record => 
        record.id === editingRecord 
          ? { ...record, ...editFormData }
          : record
      );
      setRecords(updatedRecords);
      setFilteredRecords(updatedRecords);
      
      setEditingRecord(null);
      setEditFormData({});
      alert('記錄已成功更新');
    } catch (error) {
      console.error('更新記錄失敗:', error);
      alert('更新失敗，請重試');
    }
  };

  const handleCancelEdit = () => {
    setEditingRecord(null);
    setEditFormData({});
  };

  const handleFieldChange = (key, value) => {
    setEditFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const exportRecord = async (record) => {
    try {
      const exportData = {
        掃描時間: formatDate(record.timestamp),
        備註: record.note || '無',
        ...record
      };
      
      // Remove internal fields
      delete exportData.userId;
      delete exportData.userEmail;
      delete exportData.userName;
      delete exportData.imageUrl;
      delete exportData.timestamp;
      delete exportData.note;
      delete exportData.id;
      delete exportData.originalQRData;
      delete exportData.isQRImport;

      const authHeader = await getAuthHeader();
      const response = await fetch(`${API_BASE}/json-to-excel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader,
        },
        body: JSON.stringify({
          data: [exportData],
          filename: `business_card_${record.id}`
        }),
      });

      if (!response.ok) {
        throw new Error('Excel 轉換失敗');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `business_card_${record.id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('匯出Excel失敗，請稍後再試');
    }
  };

  const exportAllRecords = async () => {
    try {
      const exportData = records.map(record => ({
        掃描時間: formatDate(record.timestamp),
        備註: record.note || '無',
        ...Object.fromEntries(
          Object.entries(record).filter(([key]) => 
            !['userId', 'userEmail', 'userName', 'imageUrl', 'timestamp', 'note', 'id', 'originalQRData', 'isQRImport'].includes(key)
          )
        )
      }));

      const authHeader = await getAuthHeader();
      const response = await fetch(`${API_BASE}/json-to-excel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader,
        },
        body: JSON.stringify({
          data: exportData,
          filename: `all_business_cards_${new Date().toISOString().split('T')[0]}`
        }),
      });

      if (!response.ok) {
        throw new Error('Excel 轉換失敗');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `all_business_cards_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('匯出Excel失敗，請稍後再試');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div>載入歷史紀錄中...</div>
      </div>
    );
  }

  return (
    <div className="history-container">
      <div className="history-header">
        <h2>名片掃描歷史紀錄</h2>
        <div className="header-actions">
          {records.length > 0 && (
            <>
              <span className="record-count">
                共 {filteredRecords.length} 筆記錄 {filteredRecords.length !== records.length && `(總計 ${records.length} 筆)`}
              </span>
              <button onClick={exportAllRecords} className="export-all-btn">
                匯出Excel
              </button>
            </>
          )}
        </div>
      </div>

      {records.length === 0 ? (
        <div className="no-records">
          <p>尚無掃描紀錄</p>
        </div>
      ) : (
        <>
          <RecordFilter records={records} onFilterChange={handleFilterChange} />
          
          {filteredRecords.length === 0 ? (
            <div className="no-filtered-records">
              <p>沒有符合篩選條件的記錄</p>
            </div>
          ) : (
            <div className="records-list">
              {currentRecords.map((record, index) => (
              <div key={record.id} className="record-row">
                <div className="record-summary" onClick={() => setSelectedRecord(selectedRecord === record.id ? null : record.id)}>
                  <div className="record-number">{startIndex + index + 1}</div>
                  <div className="record-main-info">
                    <div className="record-name">{getDisplayName(record)}</div>
                    <div className="record-date">{formatDate(record.timestamp)}</div>
                  </div>
                  <div className="record-actions">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditRecord(record);
                      }}
                      className="action-btn edit-btn"
                      title="編輯"
                    >
                      ✏️
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteRecord(record.id);
                      }}
                      className="action-btn delete-btn"
                      title="刪除"
                    >
                      🗑️
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        exportRecord(record);
                      }}
                      className="action-btn export-btn-small"
                      title="匯出Excel"
                    >
                      📊
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setManagingTags(record);
                      }}
                      className="action-btn tag-btn"
                      title="標籤管理"
                    >
                      🏷️
                    </button>
                    <div className="expand-icon">
                      {selectedRecord === record.id ? '▼' : '▶'}
                    </div>
                  </div>
                </div>
                
                {selectedRecord === record.id && (
                  <div className="record-details-expanded">
                    {record.imageUrl && (
                      <div className="record-image">
                        <img src={record.imageUrl} alt="Business card" />
                      </div>
                    )}
                    
                    <div className="record-fields">
                      {Object.entries(record).map(([key, value]) => {
                        if (['id', 'userId', 'userEmail', 'userName', 'imageUrl', 'timestamp', 'originalQRData', 'isQRImport'].includes(key)) {
                          return null;
                        }
                        return (
                          <div key={key} className="record-field">
                            <strong>{key}:</strong> <span>{value}</span>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="contact-actions-section">
                      <h4>快速聯繫</h4>
                      <ContactActions record={record} className="record-contact-actions" />
                    </div>
                  </div>
                )}
              </div>
              ))}
            </div>
          )}

          {/* 標籤管理模態框 */}
          {managingTags && (
            <div className="modal-overlay" onClick={() => setManagingTags(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>標籤管理</h3>
                  <button onClick={() => setManagingTags(null)} className="close-btn">✕</button>
                </div>
                <div className="modal-body">
                  <TagManager 
                    record={managingTags} 
                    onTagsUpdate={(tags) => handleTagsUpdate(managingTags.id, tags)}
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* 團隊管理 */}
          {showTeamManager && (
            <TeamManager onClose={() => setShowTeamManager(false)} />
          )}
          
          {/* 分享到團隊模態框 */}
          {sharingRecord && (
            <ShareToTeamModal 
              record={sharingRecord} 
              onClose={() => setSharingRecord(null)}
            />
          )}
          
          {/* 編輯模態框 */}
          {editingRecord && (
            <div className="edit-modal-overlay" onClick={handleCancelEdit}>
              <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
                <div className="edit-modal-header">
                  <h3>編輯記錄</h3>
                  <button onClick={handleCancelEdit} className="close-btn">✕</button>
                </div>
                
                <div className="edit-modal-body">
                  {Object.entries(editFormData).map(([key, value]) => (
                    <div key={key} className="edit-field">
                      <label>{key}:</label>
                      <input
                        type="text"
                        value={value || ''}
                        onChange={(e) => handleFieldChange(key, e.target.value)}
                        className="edit-input"
                      />
                    </div>
                  ))}
                </div>
                
                <div className="edit-modal-footer">
                  <button onClick={handleCancelEdit} className="cancel-btn">取消</button>
                  <button onClick={handleSaveEdit} className="save-btn">保存</button>
                </div>
              </div>
            </div>
          )}

          
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-btn"
              >
                上一頁
              </button>
              
              <div className="page-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`page-number ${currentPage === page ? 'active' : ''}`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              
              <button 
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                下一頁
              </button>
            </div>
          )}
        </>
      )}

      <style jsx="true">{`
        .history-container {
          padding: 30px;
          max-width: 1200px;
          margin: 0 auto;
          background: transparent;
          min-height: calc(100vh - 120px);
          position: relative;
        }

        .history-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .record-count {
          color: rgba(108, 99, 143, 0.7);
          font-size: 14px;
          font-weight: 500;
        }

        .export-all-btn {
          background: linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 25px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          box-shadow: 0 6px 15px rgba(86, 171, 47, 0.3);
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .export-all-btn::before {
          content: '📊';
          margin-right: 6px;
        }

        .export-all-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(86, 171, 47, 0.4);
        }

        .no-records {
          text-align: center;
          padding: 40px;
          color: #6c757d;
        }

        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 40px;
          font-size: 18px;
        }

        .records-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .record-row {
          background: rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 
            0 4px 16px rgba(31, 38, 135, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
          transition: all 0.3s ease;
        }

        .record-row:hover {
          background: rgba(255, 255, 255, 0.35);
          border-color: rgba(255, 255, 255, 0.6);
          box-shadow: 
            0 6px 20px rgba(31, 38, 135, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.7);
        }

        .record-summary {
          display: flex;
          align-items: center;
          padding: 16px 20px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .record-summary:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .record-number {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 50%;
          font-weight: 600;
          font-size: 14px;
          margin-right: 16px;
          flex-shrink: 0;
        }

        .record-main-info {
          flex: 1;
          min-width: 0;
        }

        .record-name {
          font-size: 16px;
          font-weight: 600;
          color: rgba(108, 99, 143, 0.9);
          margin-bottom: 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .record-date {
          font-size: 13px;
          color: rgba(108, 99, 143, 0.6);
          font-weight: 500;
        }

        .record-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .action-btn {
          border: none;
          border-radius: 6px;
          padding: 6px 8px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s ease;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .action-btn:hover {
          transform: translateY(-1px);
          background: rgba(255, 255, 255, 0.4);
        }

        .edit-btn:hover {
          box-shadow: 0 2px 8px rgba(255, 193, 7, 0.4);
        }

        .delete-btn:hover {
          box-shadow: 0 2px 8px rgba(220, 53, 69, 0.4);
        }

        .export-btn-small:hover {
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
        }

        .expand-icon {
          color: rgba(108, 99, 143, 0.6);
          font-size: 14px;
          font-weight: bold;
          transition: transform 0.2s ease;
        }

        .record-details-expanded {
          border-top: 1px solid rgba(255, 255, 255, 0.3);
          padding: 20px;
          background: rgba(255, 255, 255, 0.1);
          animation: slideDown 0.3s ease;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            max-height: 0;
            padding-top: 0;
            padding-bottom: 0;
          }
          to {
            opacity: 1;
            max-height: 1000px;
            padding-top: 20px;
            padding-bottom: 20px;
          }
        }

        .record-image {
          margin-bottom: 16px;
          text-align: center;
        }

        .record-image img {
          max-width: 300px;
          max-height: 200px;
          object-fit: contain;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .record-fields {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 12px;
        }

        .record-field {
          padding: 12px 16px;
          background: rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 10px;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        .record-field strong {
          color: rgba(108, 99, 143, 0.8);
          font-weight: 600;
          margin-right: 8px;
          display: inline-block;
          min-width: 80px;
          word-break: break-word;
        }

        .record-field span {
          color: rgba(108, 99, 143, 0.9);
          word-break: break-word;
          overflow-wrap: break-word;
        }

        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          margin-top: 30px;
          padding: 20px 0;
        }

        .page-btn {
          background: rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 10px;
          padding: 10px 16px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: rgba(108, 99, 143, 0.9);
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(31, 38, 135, 0.2);
        }

        .page-btn:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.4);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(31, 38, 135, 0.3);
        }

        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .page-numbers {
          display: flex;
          gap: 4px;
          margin: 0 16px;
        }

        .page-number {
          background: rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 8px;
          padding: 8px 12px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: rgba(108, 99, 143, 0.9);
          transition: all 0.3s ease;
          min-width: 40px;
          text-align: center;
        }

        .page-number:hover {
          background: rgba(255, 255, 255, 0.4);
          transform: translateY(-1px);
        }

        .page-number.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-color: rgba(102, 126, 234, 0.6);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        /* 編輯模態框樣式 */
        .edit-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(5px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 20px;
        }

        .edit-modal {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          max-width: 500px;
          width: 100%;
          max-height: 80vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .edit-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.3);
          background: rgba(255, 255, 255, 0.1);
        }

        .edit-modal-header h3 {
          margin: 0;
          color: rgba(108, 99, 143, 0.9);
          font-size: 18px;
          font-weight: 600;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          color: rgba(108, 99, 143, 0.6);
          padding: 4px 8px;
          border-radius: 6px;
          transition: all 0.2s ease;
        }

        .close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          color: rgba(108, 99, 143, 0.9);
        }

        .edit-modal-body {
          padding: 20px 24px;
          overflow-y: auto;
          flex: 1;
        }

        .edit-field {
          margin-bottom: 16px;
        }

        .edit-field label {
          display: block;
          margin-bottom: 6px;
          color: rgba(108, 99, 143, 0.8);
          font-weight: 600;
          font-size: 14px;
        }

        .edit-input {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          font-size: 14px;
          color: rgba(108, 99, 143, 0.9);
          transition: all 0.3s ease;
          box-sizing: border-box;
        }

        .edit-input:focus {
          outline: none;
          border-color: rgba(102, 126, 234, 0.6);
          background: rgba(255, 255, 255, 0.5);
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
        }

        .edit-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 20px 24px;
          border-top: 1px solid rgba(255, 255, 255, 0.3);
          background: rgba(255, 255, 255, 0.1);
        }

        .cancel-btn {
          background: rgba(108, 117, 125, 0.8);
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .cancel-btn:hover {
          background: rgba(108, 117, 125, 1);
          transform: translateY(-1px);
        }

        .save-btn {
          background: linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%);
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px rgba(86, 171, 47, 0.3);
        }

        .save-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(86, 171, 47, 0.4);
        }


        .history-header h2 {
          color: rgba(108, 99, 143, 0.9);
          font-size: 28px;
          font-weight: 600;
          margin: 0;
          text-shadow: none;
        }

        .no-records {
          background: rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 24px;
          padding: 50px;
          text-align: center;
          box-shadow: 
            0 8px 32px rgba(31, 38, 135, 0.37),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        .no-records p {
          font-size: 18px;
          color: rgba(108, 99, 143, 0.7);
          margin: 0;
          font-weight: 500;
        }

        .loading-container {
          color: rgba(108, 99, 143, 0.8);
          font-weight: 500;
        }

        .contact-actions-section {
          margin-top: 20px;
          padding: 16px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 12px;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.4);
        }

        .contact-actions-section h4 {
          margin: 0 0 12px 0;
          color: rgba(108, 99, 143, 0.9);
          font-size: 14px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .record-contact-actions {
          justify-content: flex-start;
        }

        .tag-btn:hover {
          box-shadow: 0 2px 8px rgba(255, 193, 7, 0.4);
        }

        .share-btn:hover {
          box-shadow: 0 2px 8px rgba(63, 81, 181, 0.4);
        }

        .team-btn {
          background: linear-gradient(135deg, #673ab7 0%, #9c27b0 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 25px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          box-shadow: 0 6px 15px rgba(103, 58, 183, 0.3);
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-right: 10px;
        }

        .team-btn::before {
          content: '👥';
          margin-right: 6px;
        }

        .team-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(103, 58, 183, 0.4);
        }

        .no-filtered-records {
          background: rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 24px;
          padding: 40px;
          text-align: center;
          box-shadow: 
            0 8px 32px rgba(31, 38, 135, 0.37),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        .no-filtered-records p {
          font-size: 16px;
          color: rgba(108, 99, 143, 0.7);
          margin: 0;
          font-weight: 500;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(5px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 2000;
          padding: 20px;
        }

        .modal-content {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          max-width: 600px;
          width: 100%;
          max-height: 80vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.3);
          background: rgba(255, 255, 255, 0.1);
        }

        .modal-header h3 {
          margin: 0;
          color: rgba(108, 99, 143, 0.9);
          font-size: 18px;
          font-weight: 600;
        }

        .modal-body {
          padding: 20px 24px;
          overflow-y: auto;
          flex: 1;
        }

        /* 移動端優化 */
        @media (max-width: 768px) {
          .history-container {
            padding: 15px;
          }

          .history-header {
            flex-direction: column;
            gap: 15px;
            text-align: center;
          }

          .history-header h2 {
            font-size: 24px;
          }

          .header-actions {
            flex-direction: column;
            gap: 10px;
          }

          .export-all-btn {
            width: 100%;
            padding: 14px 20px;
          }

          .record-summary {
            padding: 12px 16px;
          }

          .record-number {
            width: 35px;
            height: 35px;
            font-size: 13px;
            margin-right: 12px;
          }

          .record-name {
            font-size: 15px;
          }

          .record-date {
            font-size: 12px;
          }

          .record-details-expanded {
            padding: 16px;
          }

          .record-fields {
            grid-template-columns: 1fr;
            gap: 10px;
          }

          .record-field {
            padding: 10px 12px;
          }

          .record-field strong {
            min-width: 70px;
            font-size: 14px;
          }

          .record-image img {
            max-width: 100%;
            max-height: 150px;
          }

          .pagination {
            margin-top: 20px;
            padding: 15px 0;
          }

          .page-numbers {
            margin: 0 8px;
          }

          .page-btn {
            padding: 8px 12px;
            font-size: 13px;
          }

          .page-number {
            padding: 6px 10px;
            font-size: 13px;
            min-width: 35px;
          }
        }
      `}</style>
    </div>
  );
};

export default HistoryView;